import React, { useEffect, useRef, useState } from 'react';
import { Building2, ChevronDown, ChevronRight, Loader2, Plus, Search } from 'lucide-react';
import { Button } from '@foundation/components/ui/button';
import { Input } from '@foundation/components/ui/input';
import { Label } from '@foundation/components/ui/label';
import type { OnboardingStepProps } from '../OnboardingWizard';
import { useToast } from '@foundation/hooks/use-toast';
import {
  getPendingInvites,
  respondToInvite,
  type PendingInvite as BasePendingInvite,
} from '@foundation/services/inviteService';

// Extend the base type to include role_code returned by our Revisr invite override
type PendingInvite = BasePendingInvite & {
  role_code?: string | null;
  role_name?: string | null;
  role?: string | null;        // alias populated by the backend (equals role_code)
};

type View = 'select' | 'create';

const WorkspaceCreationStep: React.FC<OnboardingStepProps> = ({
  userId,
  onNext,
  onBack,
  onboardingMode,
  inviteRoleCode,
  pendingInviteCode,
  pendingTenantId,
}) => {
  // 'member' role → can only join the invited organisation, never create a new one
  // 'manager' role or no role → can both join and create
  const canCreateWorkspace = inviteRoleCode !== 'member';
  const { toast } = useToast();

  // Invite state
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvite, setSelectedInvite] = useState<PendingInvite | null>(null);
  const [joining, setJoining] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Tracks whether the user has manually picked an invite, so the auto-select
  // effect never overrides their choice.
  const userSelectedRef = useRef(false);

  // Create organisation state
  const [view, setView] = useState<View>('select');
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceDescription, setWorkspaceDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [creating, setCreating] = useState(false);

  // Fetch pending invites. Cancellation-safe so that re-runs (e.g. userId or
  // onboardingMode arriving after mount) never let a stale fetch overwrite a
  // newer one.
  useEffect(() => {
    let cancelled = false;
    setInvitesLoading(true);
    getPendingInvites()
      .then(all => {
        if (cancelled) return;
        const filtered =
          onboardingMode === 'organisation_only'
            ? all.filter(i => i.invite_type === 'organisation')
            : all;
        setInvites(filtered);
      })
      .catch(err => {
        if (!cancelled) console.error('Error fetching invites:', err);
      })
      .finally(() => {
        if (!cancelled) setInvitesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, onboardingMode]);

  // Auto-select an organisation once invites are available:
  //   1. The org the user arrived with via the invite link (by code, then tenant id).
  //   2. Otherwise, the most recently received pending invitation.
  // Derived from the loaded invite list rather than from a single fetch's
  // resolution, so it runs deterministically whenever invites load or the
  // invite-link props arrive. Skips once the user has manually chosen.
  useEffect(() => {
    if (userSelectedRef.current || invites.length === 0) return;
    const latestInvite = [...invites].sort(
      (a, b) =>
        new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
    )[0];
    const invitedOrg =
      (pendingInviteCode && invites.find(i => i.invite_code === pendingInviteCode)) ||
      (pendingTenantId && invites.find(i => i.tenant_id === pendingTenantId)) ||
      latestInvite ||
      null;
    if (invitedOrg) setSelectedInvite(invitedOrg);
  }, [invites, pendingInviteCode, pendingTenantId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredInvites = invites.filter(inv =>
    (inv.tenant_name ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectInvite = (inv: PendingInvite) => {
    userSelectedRef.current = true;
    setSelectedInvite(inv);
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleJoin = async () => {
    if (!selectedInvite) return;
    setJoining(true);
    try {
      await respondToInvite(selectedInvite.invite_code, 'accept');
      toast({
        title: 'Joined!',
        description: `You've joined ${selectedInvite.tenant_name ?? 'the organisation'}.`,
      });
      onNext({ hasAcceptedInvites: true });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to accept invite',
        variant: 'destructive',
      });
    } finally {
      setJoining(false);
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceName.trim()) {
      setNameError('Organisation name is required');
      return;
    }
    if (workspaceName.trim().length < 2) {
      setNameError('Must be at least 2 characters');
      return;
    }
    setCreating(true);
    onNext({
      workspaceName: workspaceName.trim(),
      workspaceDescription: workspaceDescription.trim() || undefined,
      workspaceType: 'organisation',
    });
  };

  if (invitesLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Guard: member role must not reach the create view
  if (view === 'create' && !canCreateWorkspace) setView('select');

  // ── Create organisation view ───────────────────────────────────────────────
  if (view === 'create') {
    return (
      <div className="flex flex-col items-center gap-6 py-4">
        <div className="h-16 w-16 rounded-2xl bg-primary flex items-center justify-center shadow-md">
          <Plus className="h-8 w-8 text-primary-foreground" />
        </div>

        <div className="text-center space-y-1">
          <h2 className="text-2xl font-bold text-primary">Create Organisation</h2>
          <p className="text-sm text-muted-foreground">
            Set up your organisation for team collaboration
          </p>
        </div>

        <form onSubmit={handleCreate} className="w-full max-w-md space-y-5">
          <div className="space-y-2">
            <Label htmlFor="wsName" className="font-semibold">
              Organisation Name <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="wsName"
                placeholder="e.g. My Organisation"
                value={workspaceName}
                onChange={e => {
                  setWorkspaceName(e.target.value);
                  if (nameError) setNameError('');
                }}
                className={`pl-9 rounded-xl ${nameError ? 'border-destructive' : ''}`}
                autoFocus
              />
            </div>
            {nameError && <p className="text-xs text-destructive">{nameError}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="wsDesc" className="font-semibold">
              Description{' '}
              <span className="text-muted-foreground font-normal">(Optional)</span>
            </Label>
            <textarea
              id="wsDesc"
              placeholder="Brief description of your organisation and its purpose"
              value={workspaceDescription}
              onChange={e => setWorkspaceDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setView('select')}
              className="flex-1 rounded-xl font-semibold"
            >
              Back to Organisations
            </Button>
            <Button
              type="submit"
              disabled={creating}
              className="flex-1 rounded-xl font-semibold gap-2"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Organisation
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // ── Select / invite view (default) ────────────────────────────────────────
  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-foreground">Choose Your Organisation</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
          {canCreateWorkspace
            ? 'Join an existing organisation or create a new one to organise your policies and streamline team collaboration.'
            : 'Accept your invitation to join the organisation you were invited to.'}
        </p>
      </div>

      <div className="w-full max-w-md space-y-4">
        {/* Pending Invitations */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">Your Pending Invitations</p>

          <div ref={dropdownRef} className="relative">
            {/* Dropdown trigger */}
            <button
              type="button"
              onClick={() => setDropdownOpen(prev => !prev)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-all duration-150 ${
                dropdownOpen
                  ? 'border-primary ring-2 ring-primary/20 bg-background'
                  : 'border-border bg-background hover:border-primary/50'
              }`}
            >
              <span className={selectedInvite ? 'text-foreground' : 'text-muted-foreground'}>
                {selectedInvite
                  ? selectedInvite.tenant_name ?? 'Organisation'
                  : "Select an organisation you've been invited to..."}
              </span>
              {dropdownOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
            </button>

            {/* Dropdown panel */}
            {dropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-background border border-border rounded-xl shadow-lg z-20 overflow-hidden">
                {/* Search */}
                <div className="p-2 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search organisations..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-sm bg-muted/50 rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-primary/30"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Invite list */}
                <div className="max-h-48 overflow-y-auto">
                  {filteredInvites.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4 px-3">
                      No pending invitations. Create a new organisation below.
                    </p>
                  ) : (
                    filteredInvites.map(inv => (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() => handleSelectInvite(inv)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors text-left"
                      >
                        <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">
                            {inv.tenant_name ?? 'Organisation'}
                          </p>
                          {inv.role && (
                            <p className="text-xs text-muted-foreground capitalize">
                              {inv.role.replace(/_/g, ' ')}
                            </p>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Create option at bottom of dropdown — hidden for member role */}
                {canCreateWorkspace && (
                  <div className="p-2 border-t border-border">
                    <button
                      type="button"
                      onClick={() => {
                        setDropdownOpen(false);
                        setView('create');
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-foreground border-2 border-dashed border-border rounded-lg hover:border-primary/50 hover:bg-primary/[0.02] transition-all"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Create New Organisation
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Join button — shown when an invite is selected */}
          {selectedInvite && (
            <Button
              onClick={handleJoin}
              disabled={joining}
              className="w-full gap-2 rounded-xl"
            >
              {joining ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Building2 className="h-4 w-4" />
              )}
              {joining ? 'Joining...' : `Join ${selectedInvite.tenant_name ?? 'Organisation'}`}
            </Button>
          )}
        </div>

        {/* Back button */}
        {onBack && (
          <Button
            variant="outline"
            onClick={onBack}
            className="w-full rounded-xl font-semibold"
          >
            Back
          </Button>
        )}

        {/* Or separator + Create New Organisation — hidden for member role */}
        {canCreateWorkspace && (
          <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-xs text-muted-foreground">or</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setView('create')}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-border text-sm font-semibold text-foreground hover:border-primary/50 hover:bg-primary/[0.02] transition-all duration-200"
            >
              <Plus className="h-4 w-4" />
              Create New Organisation
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default WorkspaceCreationStep;
